// AGC: FarmProHub Revenues Page
// AGC: Detailed revenue breakdown by project

import React, { useState } from 'react'
import { useAccount, useWriteContract, useWaitForTransactionReceipt } from 'wagmi'
import { motion } from 'framer-motion'
import { TrendingUp, DollarSign, Calendar, ArrowRight, Wallet, PieChart, ExternalLink } from 'lucide-react'
import { useRevenues } from '../hooks/useRevenues'
import { parseAbi, formatEther, formatUnits } from 'viem'

// AGC: StakingRevenueShare contract for claiming
const REVENUE_SHARE_ADDRESS = (import.meta.env.VITE_STAKING_REVENUE_SHARE_ADDRESS || '0x0000000000000000000000000000000000000000') as `0x${string}`
const REVENUE_SHARE_ABI = parseAbi([
  'function claimRewards(uint256 week) external',
])

// AGC: Week calculation
const WEEK_EPOCH = 1704067200
const WEEK_DURATION = 7 * 24 * 60 * 60
const getCurrentWeek = (): bigint => {
  const now = Math.floor(Date.now() / 1000)
  return BigInt(Math.floor((now - WEEK_EPOCH) / WEEK_DURATION))
}

// AGC: Format currency helper
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

// AGC: BaseScan URL helper
const getBaseScanUrl = (address: string) => {
  const network = import.meta.env.VITE_BASE_NETWORK || 'base-sepolia'
  if (network === 'base' || network === 'base-mainnet') {
    return `https://basescan.org/address/${address}`
  }
  return `https://sepolia.basescan.org/address/${address}`
}

export const RevenuesPage: React.FC = () => {
  const { address, isConnected } = useAccount()
  const { stats, refresh } = useRevenues()
  const [timeRange, setTimeRange] = useState<'all' | 'month' | 'week'>('all')
  const [isClaiming, setIsClaiming] = useState(false)
  
  const { writeContractAsync } = useWriteContract()
  const [claimHash, setClaimHash] = useState<`0x${string}` | undefined>()
  
  useWaitForTransactionReceipt({
    hash: claimHash,
    onSuccess: () => {
      setIsClaiming(false)
      setClaimHash(undefined)
      refresh()
    },
  })

  // AGC: Handle claim rewards
  const handleClaimAll = async () => {
    if (!isConnected || !address) return
    
    try {
      setIsClaiming(true)
      const currentWeek = getCurrentWeek()
      const hash = await writeContractAsync({
        address: REVENUE_SHARE_ADDRESS,
        abi: REVENUE_SHARE_ABI,
        functionName: 'claimRewards',
        args: [currentWeek],
      })
      setClaimHash(hash)
    } catch (error: any) {
      console.error('Failed to claim rewards:', error)
      setIsClaiming(false)
      alert(error.message || 'Failed to claim rewards')
    }
  }

  return (
    <main className="pt-[72px] pb-[88px] px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* AGC: Page Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-4"
        >
          <h1 className="text-2xl font-bold text-white mb-2 flex items-center justify-center gap-2">
            <TrendingUp className="w-7 h-7 text-[#0072CE]" />
            Revenue Dashboard
          </h1>
          <p className="text-slate-400 text-sm">
            Track revenues from all FarmProHub projects
          </p>
        </motion.div>

        {/* AGC: Total Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 sm:grid-cols-4 gap-3"
        >
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50 text-center">
            <DollarSign className="w-5 h-5 text-[#0072CE] mx-auto mb-2" />
            <p className="text-xs text-slate-400">Total Revenue</p>
            <p className="text-lg font-bold text-white">{formatCurrency(stats.totalRevenue)}</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50 text-center">
            <PieChart className="w-5 h-5 text-[#0072CE] mx-auto mb-2" />
            <p className="text-xs text-slate-400">Stakers Share (50%)</p>
            <p className="text-lg font-bold text-[#0072CE]">{formatCurrency(stats.stakersShare)}</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50 text-center">
            <Wallet className="w-5 h-5 text-blue-400 mx-auto mb-2" />
            <p className="text-xs text-slate-400">Your Share</p>
            <p className="text-lg font-bold text-white">{formatCurrency(stats.yourShare)}</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50 text-center">
            <ArrowRight className="w-5 h-5 text-yellow-400 mx-auto mb-2" />
            <p className="text-xs text-slate-400">Claimable</p>
            <p className="text-lg font-bold text-yellow-400">{formatCurrency(stats.claimable)}</p>
          </div>
        </motion.div>

        {/* AGC: Your Earnings (if connected) */}
        {isConnected && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-gradient-to-r from-[#003a5e]/30 to-[#003a5e]/30 rounded-xl p-5 border border-[#0072CE]/20"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-400">Your Claimable Earnings</p>
                <p className="text-3xl font-bold text-white">{formatCurrency(stats.claimable)}</p>
                <p className="text-xs text-slate-500">From all revenue-sharing projects</p>
              </div>
              <button
                onClick={handleClaimAll}
                disabled={isClaiming || stats.claimable <= 0}
                className="px-6 py-3 bg-[#005a9e] hover:bg-[#004a7e] text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isClaiming ? 'Claiming...' : 'Claim All'}
              </button>
            </div>
          </motion.div>
        )}

        {/* AGC: Time Range Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex justify-center"
        >
          <div className="flex bg-slate-800/50 rounded-lg p-1 border border-slate-700/50">
            <button
              onClick={() => setTimeRange('week')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                timeRange === 'week' ? 'bg-[#005a9e] text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              This Week
            </button>
            <button
              onClick={() => setTimeRange('month')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                timeRange === 'month' ? 'bg-[#005a9e] text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              This Month
            </button>
            <button
              onClick={() => setTimeRange('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                timeRange === 'all' ? 'bg-[#005a9e] text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              All Time
            </button>
          </div>
        </motion.div>

        {/* AGC: Marketplace Revenue Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50"
        >
          <h3 className="text-white font-semibold mb-4">Marketplace Revenue Sources</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="text-xl">🛡️</span>
                <div>
                  <p className="text-white font-medium text-sm">Buff1155 Marketplace</p>
                  <a
                    href={getBaseScanUrl(import.meta.env.VITE_BUFF1155_ADDRESS || '')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#0072CE] hover:text-[#0088e6] flex items-center gap-1"
                  >
                    View Contract <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-400">Pending Collection</p>
                <p className="text-sm font-semibold text-white">
                  {formatCurrency(
                    (Number(formatEther(stats.marketplaceRevenue.buff1155.eth)) * 3000) +
                    (Number(formatEther(stats.marketplaceRevenue.buff1155.ogb)) * 0.01) +
                    Number(formatUnits(stats.marketplaceRevenue.buff1155.usdc, 6))
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="text-xl">🧪</span>
                <div>
                  <p className="text-white font-medium text-sm">ClassicPotion1155 Marketplace</p>
                  <a
                    href={getBaseScanUrl(import.meta.env.VITE_CLASSIC_POTION1155_ADDRESS || '')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#0072CE] hover:text-[#0088e6] flex items-center gap-1"
                  >
                    View Contract <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-400">Pending Collection</p>
                <p className="text-sm font-semibold text-white">
                  {formatCurrency(
                    (Number(formatEther(stats.marketplaceRevenue.classicPotion1155.eth)) * 3000) +
                    (Number(formatEther(stats.marketplaceRevenue.classicPotion1155.ogb)) * 0.01) +
                    Number(formatUnits(stats.marketplaceRevenue.classicPotion1155.usdc, 6))
                  )}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* AGC: Project Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="space-y-4"
        >
          <h3 className="text-white font-semibold">Revenue by Project</h3>
          
          {stats.projects.map((project) => (
            <div
              key={project.id}
              className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center text-2xl">
                    {project.icon}
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">{project.name}</h4>
                    <span className={`text-xs ${project.status === 'live' ? 'text-[#0072CE]' : 'text-yellow-400'}`}>
                      {project.status === 'live' ? '● Live' : '○ Upcoming'}
                    </span>
                  </div>
                </div>
                <a 
                  href={project.id === 'origami' ? 'https://origamibattle.app' : '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-purple-400 hover:text-purple-300 flex items-center gap-1"
                >
                  Visit <ArrowRight className="w-3 h-3" />
                </a>
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <p className="text-xs text-slate-400">Total Revenue</p>
                  <p className="text-lg font-bold text-white">{formatCurrency(project.totalRevenue)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400">Stakers Share</p>
                  <p className="text-lg font-bold text-[#0072CE]">{formatCurrency(project.stakersShare)}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-400 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> This Week
                  </p>
                  <p className="text-lg font-bold text-white">{formatCurrency(project.thisMonth)}</p>
                </div>
              </div>
            </div>
          ))}

          {/* AGC: More Projects Coming */}
          <div className="bg-slate-800/30 rounded-xl p-5 border border-dashed border-slate-600 text-center">
            <p className="text-slate-500">More revenue-sharing projects coming soon...</p>
            <p className="text-xs text-slate-600 mt-1">Each new project will add to the stakers' revenue share</p>
          </div>
        </motion.div>

        {/* AGC: Revenue Distribution Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50"
        >
          <h3 className="text-white font-semibold mb-3">Revenue Distribution</h3>
          <ul className="space-y-2 text-sm text-slate-400">
            <li className="flex items-start gap-2">
              <span className="text-[#0072CE]">•</span>
              50% of all project revenue goes to stakers
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#0072CE]">•</span>
              Your share is proportional to your staked amount
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#0072CE]">•</span>
              Revenue is distributed automatically and can be claimed anytime
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#0072CE]">•</span>
              Revenue from all projects is combined into a single pool
            </li>
          </ul>
        </motion.div>
      </div>
    </main>
  )
}

